  // Enhanced script with validation caching and cart state tracking

  // Cache for validation results

  let validationCache = {};
  let lastCartState = null;
  let isValidationInProgress = false;
 
  // Store applied discount code in localStorage for tracking
  function setAppliedDiscountCode(code) {
    if (typeof Storage !== 'undefined') {
      localStorage.setItem('appliedDiscountCode', code);
    }
  }

  function getAppliedDiscountCode() {
    if (typeof Storage !== 'undefined') {
      return localStorage.getItem('appliedDiscountCode');
    }
    return null;
  }

  function clearAppliedDiscountCode() {
    if (typeof Storage !== 'undefined') {
      localStorage.removeItem('appliedDiscountCode');
    } 
  }

  // Get cart state for comparison
  async function getCartState() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await response.json();
      return {
        itemCount: cart.item_count,
        totalPrice: cart.total_price,
        lineItems: cart.items.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          variant_id: item.variant_id,
        })),
      };
    } catch (error) {
      console.error('Error getting cart state:', error);
      return null;
    }
  }

  // Compare cart states to determine if validation cache should be cleared
  function hasCartChanged(currentState, previousState) {
    if (!previousState || !currentState) return true;

    // Check basic properties
    if (currentState.itemCount !== previousState.itemCount || currentState.totalPrice !== previousState.totalPrice) {
      return true;
    }

    // Check line items
    if (currentState.lineItems.length !== previousState.lineItems.length) {
      return true;
    }

    // Check each line item
    for (let i = 0; i < currentState.lineItems.length; i++) {
      const current = currentState.lineItems[i];
      const previous = previousState.lineItems[i];

      if (
        current.id !== previous.id ||
        current.quantity !== previous.quantity ||
        current.variant_id !== previous.variant_id
      ) {
        return true;
      }
    }

    return false;
  }

  // Clear validation cache when cart changes
  function clearValidationCache() {
    validationCache = {};
    console.log('Validation cache cleared due to cart changes');
  }

  // Show/Hide loader functions
  function showLoader(button) {
    const loader = button.querySelector('.loader');
    const btnText = button.querySelector('.btn-text');
    if (loader && btnText) {
      loader.classList.add('show');
      button.disabled = true;
    }
  }

  function hideLoader(button) {
    const loader = button.querySelector('.loader');
    const btnText = button.querySelector('.btn-text');
    if (loader && btnText) {
      loader.classList.remove('show');
      button.disabled = false;
    }
  }

  // Show/Hide popup validation loader
  function showPopupLoader() {
    const popupLoader = document.getElementById('popupValidationLoader');
    const popupContent = document.getElementById('popupContent');
    if (popupLoader && popupContent) {
      popupLoader.style.display = 'flex';
      popupContent.style.display = 'none';
    }
  }

  function hidePopupLoader() {
    const popupLoader = document.getElementById('popupValidationLoader');
    const popupContent = document.getElementById('popupContent');
    if (popupLoader && popupContent) {
      popupLoader.style.display = 'none';
      popupContent.style.display = 'block';
    }
  }

  // Function to validate a single discount code
  async function validateDiscountCode(code) {
    if (validationCache[code]) {
      console.log(`Using cached validation for ${code}:`, validationCache[code]);
      return validationCache[code];
    }

    try {
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();

      if (cart.item_count === 0) {
        const result = { valid: false, reason: 'empty_cart' };
        validationCache[code] = result;
        return result;
      }

      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ discount: code }),
      });

      const checkResponse = await response.json();

      // Immediately restore cart state
      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ discount: '' }),
      });
      const isApplicable =
        Array.isArray(checkResponse.discount_codes) &&
        checkResponse.discount_codes.length > 0 &&
        checkResponse.discount_codes[0].applicable === true;

      const result = isApplicable ? { valid: true } : { valid: false, reason: 'not_applicable' };

      validationCache[code] = result;
      return result;
    } catch (error) {
      console.error('Error validating discount code:', error);
      const result = { valid: false, reason: 'error' };
      validationCache[code] = result;
      return result;
    }
  }

  // Function to update discount code item visual state
  function updateDiscountCodeState(codeElement, result) {
    const { valid, reason } = result;
    const button = codeElement.querySelector('.apply-code-btn');
    const btnText = button?.querySelector('.btn-text');
    const statusElement = codeElement.querySelector('.code-status');
    const statusText = statusElement?.querySelector('.status-text');

    codeElement.classList.remove('valid', 'invalid', 'checking');
    statusElement?.classList.remove('valid', 'invalid', 'checking');

    if (valid) {
      codeElement.classList.add('valid');
      if (statusElement) statusElement.style.display = 'none';
      if (button) button.disabled = false;
      if (btnText) btnText.textContent = 'Apply';
    } else {
      codeElement.classList.add('invalid');
      statusElement?.classList.add('invalid');
      if (statusElement) statusElement.style.display = 'block';
      if (button) button.disabled = true;
      if (btnText) btnText.textContent = '';

      if (statusText) {
        statusText.textContent =
          {
            empty_cart: 'Add Items First',
            invalid_code: 'Expired',
            not_applicable: 'Not Applicable',
            error: 'Unavailable',
          }[reason] || 'Unavailable';
      }
    }
  }

  // Function to apply cached validation results instantly
  function applyCachedValidation() {
    document.querySelectorAll('.discount-code-item').forEach((item) => {
      const code = item.dataset.code;
      if (code && validationCache[code]) {
        updateDiscountCodeState(item, validationCache[code]);
      }
    });
  }

  // OPTIMIZED: Function to validate all discount codes using Promise.all
  async function validateAllDiscountCodes() {
    const codeItems = document.querySelectorAll('.discount-code-item');
    if (!codeItems.length) {
      hidePopupLoader();
      return;
    }

    const currentCartState = await getCartState();
    const cartChanged = hasCartChanged(currentCartState, lastCartState);

    if (cartChanged) {
      clearValidationCache();
      lastCartState = currentCartState;
    }

    const codesToValidate = [];
    const codeElements = {};

    codeItems.forEach((item) => {
      const code = item.dataset.code;
      if (code) {
        codeElements[code] = item;
        if (!validationCache[code]) {
          codesToValidate.push(code);
          // Mark as checking
          item.classList.add('checking');
          const status = item.querySelector('.code-status');
          const statusText = status?.querySelector('.status-text');
          status?.classList.add('checking');
          if (status) status.style.display = 'block';
          if (statusText) statusText.textContent = 'Checking...';
        }
      }
    });

    showPopupLoader();
    isValidationInProgress = true;

    // Apply any cached results instantly
    applyCachedValidation();

    // Validate uncached codes
    if (codesToValidate.length > 0) {
      try {
        const results = await Promise.all(
          codesToValidate.map((code) => validateDiscountCode(code).then((result) => ({ code, result })))
        );

        results.forEach(({ code, result }) => {
          const item = codeElements[code];
          if (item) updateDiscountCodeState(item, result);
        });

        console.log('All discount codes validated.');
      } catch (error) {
        console.error('Error during discount validation:', error);
        codesToValidate.forEach((code) => {
          if (!validationCache[code]) {
            updateDiscountCodeState(codeElements[code], { valid: false, reason: 'error' });
          }
        });
      }
    }

    hidePopupLoader();
    isValidationInProgress = false;
  }

  // Enhanced popup open function with caching logic
  function openDiscountPopup() {
    document.getElementById('discountPopupOverlay')?.classList.add('active');
    document.getElementById('discountPopup')?.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Start validation process (will use cache if available)
    validateAllDiscountCodes();
  }

  function closeDiscountPopup() {
    document.getElementById('discountPopupOverlay')?.classList.remove('active');
    document.getElementById('discountPopup')?.classList.remove('active');
    document.body.style.overflow = '';
  }

  function applyDiscountFromPopup(code, button) {
    // Check if button is disabled (invalid code)
    if (button.disabled) {
      showMessage('This discount code is not applicable to your current cart.', 'error');
      return;
    }

    const input = document.getElementById('discountCodeInput');
    if (input) input.value = code;

    showLoader(button);
    applyDiscountCode(code, button);
  }

  // Enhanced message display function
  function showMessage(message, type = 'success') {
    const container = document.getElementById('messageContainer');
    if (container) {
      const messageClass = type === 'success' ? 'success' : 'error';
      const icon = type === 'success' ? '✅' : '❌';

      container.innerHTML = `
        <div class="message ${messageClass}" style="
          padding: 12px 16px;
          border-radius: 8px;
          margin: 10px 0;
          font-size: 14px;
          line-height: 1.4; 
          background-color: ${type === 'success' ? '#d4edda' : '#f8d7da'};
          color: ${type === 'success' ? '#155724' : '#721c24'};
          border: 1px solid ${type === 'success' ? '#c3e6cb' : '#f5c6cb'};
          display: flex;
          align-items: flex-start;
          gap: 8px;
        ">
          <span style="flex-shrink: 0;">${icon}</span>
          <span>${message}</span>
        </div>
      `;

      setTimeout(
        () => {
          container.innerHTML = '';
        },
        type === 'error' ? 7000 : 5000
      );
    }
  }

  // Regular discount application function (for manual input)
  async function applyDiscountCode(customCode = null, buttonElement = null) {
    const input = document.getElementById('discountCodeInput');
    const code = customCode || (input?.value || '').trim().toUpperCase();
    const applyBtn = buttonElement || document.querySelector('.discount-btn');

    if (!code) {
      showMessage('Please enter a discount code.', 'error');
      if (buttonElement) hideLoader(buttonElement);
      return;
    } 

    if (!buttonElement && applyBtn) {
      showLoader(applyBtn);
    }

    try {
      const currentCartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const currentCart = await currentCartResponse.json();
      const initialTotal = currentCart.total_price;
      const initialDiscount = currentCart.total_discount;

      if (currentCart.item_count === 0) {
        showMessage('Your cart is empty. Add items to your cart before applying a discount code.', 'error');
        if (buttonElement) hideLoader(buttonElement);
        if (!buttonElement && applyBtn) hideLoader(applyBtn);
        return;
      }

      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: code,
        }),
      });

      if (!response.ok) {
        if (response.status === 422) {
          showMessage(
            `The discount code "${code}" is not valid, has expired, or has reached its usage limit.`,
            'error'
          );
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return;
      }

      const updatedCartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const updatedCart = await updatedCartResponse.json();

      const discountApplied =
        updatedCart.total_discount > initialDiscount ||
        (updatedCart.cart_level_discount_applications && updatedCart.cart_level_discount_applications.length > 0) ||
        updatedCart.total_price < initialTotal;

      if (discountApplied) {
        setAppliedDiscountCode(code);
        const discountAmount = updatedCart.total_discount - initialDiscount;
        const discountFormatted =
          window.Shopify && window.Shopify.formatMoney
            ? window.Shopify.formatMoney(discountAmount)
            : `$${(discountAmount / 100).toFixed(2)}`;

        showMessage(
          `🎉 Awesome! Discount code "${code}" applied successfully! You saved ${discountFormatted}!`,
          'success'
        );

        if (input) input.value = '';

        // Clear validation cache since cart will change after discount application
        clearValidationCache();

        setTimeout(() => {
          closeDiscountPopup();
          window.location.reload();
        }, 1500);
      } else {
        showMessage(`The discount code "${code}" is not applicable to the items in your current cart.`, 'error');
      }
    } catch (err) {
      console.error('Error applying discount:', err);
      showMessage('Oops! Something went wrong while applying the discount. Please try again.', 'error');
    } finally {
      if (buttonElement) hideLoader(buttonElement);
      if (!buttonElement && applyBtn) hideLoader(applyBtn);
    }
  }

  // Event listeners
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDiscountPopup();
  });

  document.addEventListener('DOMContentLoaded', () => {
    const discountInput = document.getElementById('discountCodeInput');
    if (discountInput) {
      discountInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          applyDiscountCode();
        }
      });
    }
  });








  document.addEventListener('DOMContentLoaded', async function () {
    const storedDiscountCode = localStorage.getItem('appliedDiscountCode');
    const appliedDiscountDisplay = document.getElementById('appliedDiscountDisplay');
    const appliedDiscountCodeText = document.getElementById('appliedDiscountCodeText');
    const viewDiscountEntry = document.getElementById('viewDiscountEntryMain');

    if (storedDiscountCode && appliedDiscountDisplay && appliedDiscountCodeText) {
      // Fetch cart data
      try {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartResponse.json();

        const totalDiscount = cart.total_discount || 0;
        const totalPriceBeforeDiscount = cart.original_total_price || (cart.total_price + totalDiscount);
        const discountPercentage = totalPriceBeforeDiscount > 0
          ? Math.round((totalDiscount / totalPriceBeforeDiscount) * 100)
          : 0;

        // Format discount amount (assuming AED, adjust as needed)
        const discountAmountFormatted = `-AED ${(totalDiscount / 100).toFixed(2)}`;

        // Format the display string
        let displayString = '';
if (discountPercentage > 0) {
  displayString += `
    <span class="discount-code">${storedDiscountCode}</span> applied 
    <span class="discount-amount">${discountAmountFormatted}</span> 
    <span class="discount-percentage">(${discountPercentage}% off)</span>
  `;
}


        appliedDiscountCodeText.innerHTML = displayString;
        appliedDiscountCodeText.setAttribute('title', storedDiscountCode);

        appliedDiscountDisplay.style.display = 'block';
        appliedDiscountDisplay.style.width = '100%';

        if (viewDiscountEntry) {
          viewDiscountEntry.style.display = 'none';
        }
      } catch (err) {
        // fallback to just code if cart fetch fails
        appliedDiscountCodeText.textContent = storedDiscountCode;
        appliedDiscountCodeText.setAttribute('title', storedDiscountCode);
        appliedDiscountDisplay.style.display = 'block';
        appliedDiscountDisplay.style.width = '100%';
        if (viewDiscountEntry) {
          viewDiscountEntry.style.display = 'none';
        }
      }
    } else {
      if (appliedDiscountDisplay) {
        appliedDiscountDisplay.style.display = 'none';
      }
      if (viewDiscountEntry) {
        viewDiscountEntry.style.display = 'block';
      }
    }
  });

  // Function to remove discount
  async function removeDiscountCode() {
    const removeBtn = document.querySelector('.remove-discount-btn');
    const loader = document.getElementById('removeLoader');

    // Add loading state to button
    if (removeBtn) {
      removeBtn.classList.add('loading');
      removeBtn.disabled = true;
    }

    // Show loader
    if (loader) loader.classList.add('show');

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          discount: '',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cartData = await response.json();
      console.log('Discount removed, updated cart:', cartData);

      // Clear discount from localStorage (if supported)
      if (typeof Storage !== 'undefined') {
        localStorage.removeItem('appliedDiscountCode');
      }

      // Optionally call a cleanup function if defined elsewhere
      if (typeof clearAppliedDiscountCode === 'function') {
        clearAppliedDiscountCode();
      }

      // Optionally show a success message
      if (typeof showMessage === 'function') {
        showMessage('Discount removed successfully!', 'success');
      }

      // Reload page to reflect updated cart
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Error removing discount:', err);

      // Optionally show an error message
      if (typeof showMessage === 'function') {
        showMessage('Failed to remove discount. Please try again.', 'error');
      } else {
        alert('Failed to remove discount. Please try again.');
      }
    } finally {
      // Restore button state if the reload doesn't happen
      if (removeBtn) {
        removeBtn.classList.remove('loading');
        removeBtn.disabled = false;
      }
      if (loader) loader.classList.remove('show');
    }
  }

  // Function to open discount popup
  // function openDiscountPopup() {
  //   document.getElementById('discountPopupOverlay')?.classList.add('active');
  //   document.getElementById('discountPopup')?.classList.add('active');
  //   document.body.style.overflow = 'hidden';
  // }
